import { API_BASE_URL } from '@/shared/constants/config';
import api from '@/shared/services/api';

export const zipfileService = {
  /**
   * Fetch paginated list of zipfiles
   */
  async fetchAll(params = {}) {
    const {
      offset = 0,
      limit = 100,
      orderBy = '-created_at',
      tags = null,
      project = null,
      target = null,
      batch = null
    } = params;

    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
      order_by: orderBy
    });

    // Add tag-based filtering for project/target/batch
    const tagFilters = [];
    if (project) tagFilters.push(`project:${project}`);
    if (target) tagFilters.push(`target:${target}`);
    if (batch) tagFilters.push(`batch:${batch}`);
    
    if (tagFilters.length > 0 || tags) {
      const allTags = [...tagFilters, ...(tags || [])];
      queryParams.append('tags', allTags.join(','));
    }

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/zipfiles?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch zipfiles: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get single zipfile details
   */
  async getById(zipfileId) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/zipfiles/${zipfileId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch zipfile: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Create new zipfile from FMO input IDs
   */
  async create(requests) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/zipfiles`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requests)
    });

    if (!response.ok) {
      throw new Error(`Failed to create zipfile: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update zipfiles (bulk update)
   * @param {Array} updates - Array of update objects with id_ and fields to update
   */
  async updateBulk(updates) {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/zipfiles`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update zipfiles: ${errorText || response.statusText}`);
    }

    return response.json();
  },

  /**
   * Download zipfile
   */
  async download(zipfileId, filename) {
    // Use the base endpoint for downloading zipfile
    const response = await api.get(
      `/zipfiles/${zipfileId}`,
      {
        responseType: 'blob' // Important for downloading files
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Use provided filename or fallback
    link.setAttribute('download', filename || `zipfile_${zipfileId}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    // Clean up
    window.URL.revokeObjectURL(url);
  },

  /**
   * Build tree structure from zipfiles for browser
   */
  buildTreeFromZipfiles(zipfiles) {
    const tree = new Map();
    
    zipfiles.forEach(zipfile => {
      // Extract project/target/batch from FMO input tags
      const projects = new Set();
      const targets = new Set();
      const batches = new Set();
      
      // Parse tags to extract hierarchy info
      (zipfile.tags || []).forEach(tag => {
        const [key, value] = tag.split(':');
        if (key === 'project') projects.add(value);
        if (key === 'target') targets.add(value);
        if (key === 'batch') batches.add(value);
      });

      // Build tree structure
      projects.forEach(project => {
        if (!tree.has(project)) {
          tree.set(project, {
            id: `project-${project}`,
            name: project,
            type: 'project',
            children: new Map()
          });
        }

        const projectNode = tree.get(project);
        
        targets.forEach(target => {
          if (!projectNode.children.has(target)) {
            projectNode.children.set(target, {
              id: `target-${project}-${target}`,
              name: target,
              type: 'target',
              parent: project,
              children: new Map()
            });
          }

          const targetNode = projectNode.children.get(target);
          
          batches.forEach(batch => {
            if (!targetNode.children.has(batch)) {
              targetNode.children.set(batch, {
                id: `batch-${project}-${target}-${batch}`,
                name: batch,
                type: 'batch',
                parent: target,
                count: 0
              });
            }
            targetNode.children.get(batch).count++;
          });
        });
      });
    });

    // Convert Maps to arrays for Tree component
    const convertToArray = (map) => {
      return Array.from(map.values()).map(node => ({
        ...node,
        children: node.children ? convertToArray(node.children) : undefined
      }));
    };

    return convertToArray(tree);
  }
};